import { Button, Container, Link, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { check404Error } from 'v2/core/errors/errors';
import captureError from 'v2/core/services/errors/capture';
import Error404 from 'v2/views/components/error/error404';
import { logError } from 'v2/views/layouts/utils/logError';
import { ChunkLoadError } from './ChunkLoadError';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const ErrorFallback = ({ error, resetErrorBoundary }: ErrorFallbackProps) => {
  logError(error);

  const is404Error = check404Error(error);
  const isChunkLoadError = error.name === 'ChunkLoadError';

  if (is404Error) {
    return <Error404 message={error.message} />;
  }

  if (isChunkLoadError) {
    return <ChunkLoadError />;
  }

  return (
    <Container>
      <Stack
        height="100vh"
        textAlign="center"
        alignItems="center"
        justifyContent="center"
        spacing={2}
        mt={-8}
      >
        <Typography variant="h4">Aw snap! Something went wrong</Typography>
        <Typography variant="body1">
          Brace yourself till we get the error fixed. You may also refresh the
          page or go to our home page.
        </Typography>
        <Stack spacing={3}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            justifyContent="center"
          >
            <Button variant="outlined" onClick={resetErrorBoundary}>
              Try Again
            </Button>
            <Button
              variant="contained"
              LinkComponent={Link}
              href="/"
              sx={{
                '&:hover': {
                  color: 'primary.contrastText',
                },
              }}
            >
              Go Home
            </Button>
          </Stack>
          <Typography variant="body1" color="text.secondary">
            Error Message: {error.message}
          </Typography>
        </Stack>
      </Stack>
    </Container>
  );
};

interface ErrorBoundaryProps {
  children: ReactNode;
}

const ErrorBoundary = ({ children }: ErrorBoundaryProps) => (
  <ReactErrorBoundary FallbackComponent={ErrorFallback} onError={captureError}>
    {children}
  </ReactErrorBoundary>
);

export default ErrorBoundary;
